import React, { useEffect, useState, useRef } from 'react';
import { useMap, Polyline } from 'react-leaflet';
import { getDriverColor, decodePolyline } from '../../utils/routeUtils';

/**
 * RouteDisplay Component
 * Fetches and displays the optimal route between a set of points using OSRM
 *
 * @param {Object} props - Component props
 * @param {Array} props.driverPoints - Array of points for a specific driver
 * @param {boolean} [props.optimize=false] - Whether to optimize the waypoint order
 */
const RouteDisplay = ({ driverPoints, optimize = false }) => {
  const map = useMap();
  const routingControlRef = useRef(null);
  const [polylinePositions, setPolylinePositions] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  // Get driver color for route lines
  const driverNumber = driverPoints[0]?.driverNumber || 1;
  const routeColor = getDriverColor(driverNumber);

  useEffect(() => {
    // Clean up previous routing control if it exists
    if (routingControlRef.current) {
      map.removeControl(routingControlRef.current);
      routingControlRef.current = null;
    }

    async function fetchRoute() {
      if (driverPoints.length < 2) return;

      setIsLoading(true);
      let sortedPoints = [];

      try {
        // Process the driver points
        sortedPoints = [...driverPoints];

        // OSRM has limits too, so we'll still use segmentation if needed
        // Let's limit to 100 points per request to be safe
        const MAX_WAYPOINTS_PER_REQUEST = 98;  // Max waypoints excluding origin and destination

        if (driverPoints.length <= 100) {
          // Single request is sufficient
          const positions = await fetchRouteSegment(driverPoints);
          setPolylinePositions(positions);
        } else {
          // Need to split into multiple segments
          console.log(`Route has ${driverPoints.length} points, splitting into segments`);

          // Split the route into segments, keeping HOME point at the beginning of the first segment
          // and at the end of the last segment if they exist
          const allSegments = [];
          let currentSegment = [];

          // Add first point (usually HOME) to the first segment
          currentSegment.push(driverPoints[0]);

          // Process middle points in chunks
          for (let i = 1; i < driverPoints.length - 1; i++) {
            currentSegment.push(driverPoints[i]);

            // When we reach the limit or last point, finish this segment and start a new one
            if (currentSegment.length === MAX_WAYPOINTS_PER_REQUEST + 1 || i === driverPoints.length - 2) {
              allSegments.push(currentSegment);
              // Start new segment with the last point of previous segment as first point
              currentSegment = [currentSegment[currentSegment.length - 1]];
            }
          }

          // Add the last point to the last segment
          if (driverPoints.length > 1) {
            currentSegment.push(driverPoints[driverPoints.length - 1]);
            if (currentSegment.length > 1) {
              allSegments.push(currentSegment);
            }
          }

          console.log(`Split into ${allSegments.length} segments`);

          // Fetch each segment and combine the results
          const allPositions = [];
          for (let i = 0; i < allSegments.length; i++) {
            console.log(`Fetching segment ${i+1}/${allSegments.length} with ${allSegments[i].length} points`);
            const segmentPositions = await fetchRouteSegment(allSegments[i]);

            // Add to combined route, avoiding duplicate points between segments
            if (i === 0) {
              allPositions.push(...segmentPositions);
            } else {
              // Skip the first point in subsequent segments as it duplicates the last point of the previous segment
              allPositions.push(...segmentPositions.slice(1));
            }
          }

          setPolylinePositions(allPositions);
        }
      } catch (error) {
        console.error('Error fetching route:', error);
        // Fall back to simple straight lines between points
        console.log('Falling back to straight lines due to error');
        setPolylinePositions(sortedPoints.map(p => [p.lat, p.lng]));
      } finally {
        setIsLoading(false);
      }
    }

    // Fetch a segment of the route using OSRM
    async function fetchRouteSegment(points) {
      if (points.length < 2) {
        console.log('Too few points, returning simple line');
        return points.map(p => [p.lat, p.lng]);
      }

      // Format coordinates for OSRM: [lng, lat] format
      const coordinates = points.map(point => [point.lng, point.lat]);

      try {
        console.log('OSRM coordinates:', coordinates);

        // OSRM expects coordinates in the format: lon1,lat1;lon2,lat2;lon3,lat3...
        const coordinatesString = coordinates
          .map(coord => `${coord[0]},${coord[1]}`)
          .join(';');

        // Query parameters
        const queryParams = new URLSearchParams({
          overview: 'full',
          geometries: 'geojson',
          steps: 'false',
          alternatives: 'false'
        });

        if (optimize) {
          queryParams.append('annotations', 'true');
        }

        // The standard OSRM API URL format
        const osrmUrl = `${process.env.REACT_APP_NODE_SERVER_URL}/api/routes/osrm/route/v1/driving/${coordinatesString}?${queryParams.toString()}`;

        // Make a GET request (this is the standard OSRM API format)
        const response = await fetch(osrmUrl, {
          method: 'GET',
          headers: {
            'Accept': 'application/json',
          }
        });

        console.log('OSRM response status:', response.status);

        if (!response.ok) {
          const errorText = await response.text();
          console.error('OSRM error response:', errorText);
          throw new Error(`Failed to fetch OSRM route: ${response.statusText}`);
        }

        const data = await response.json();
        console.log('OSRM response data:', data);

        if (data.code !== 'Ok') {
          console.error('OSRM returned non-Ok status:', data.code, data.message);
          throw new Error(`OSRM API error: ${data.code}`);
        }

        // Extract route points from the response
        if (!data.routes || !data.routes[0] || !data.routes[0].geometry || !data.routes[0].geometry.coordinates) {
          console.error('Malformed OSRM response - missing geometry:', data);
          throw new Error('No route found in OSRM response');
        }

        // Log route details
        console.log(`OSRM returned route with ${data.routes[0].geometry.coordinates.length} points`);
        console.log('Route distance:', data.routes[0].distance, 'meters');
        console.log('Route duration:', data.routes[0].duration, 'seconds');

        // OSRM returns coordinates in [lng, lat] format, but Leaflet needs [lat, lng]
        const routeCoordinates = data.routes[0].geometry.coordinates.map(coord => [coord[1], coord[0]]);

        // Verify we got a reasonable number of points
        if (routeCoordinates.length < 3) {
          console.warn('OSRM returned very few points, might be straight line');
        }

        return routeCoordinates;
      } catch (error) {
        console.error('OSRM request error:', error);
        // If we get an error, fall back to straight line for this segment
        console.log('Falling back to straight line for segment due to error');
        return points.map(p => [p.lat, p.lng]);
      }
    }

    fetchRoute();

    // Cleanup
    return () => {
      if (routingControlRef.current) {
        map.removeControl(routingControlRef.current);
      }
    };
  }, [driverPoints, map, driverNumber, optimize]);

  return (
    <>
      {polylinePositions.length > 0 && (
        <Polyline
          positions={polylinePositions}
          color={routeColor}
          weight={5}
          opacity={0.9}
          dashArray={isLoading ? "10, 10" : ""}
        />
      )}
    </>
  );
};

export default RouteDisplay;
